import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { pageUrl } from '../seo'
import { ExternalLink } from 'lucide-react'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

// Per-logo scale to equalise visual size (wide text logos need boosting,
// icon logos may need reduction)
const LOGO_SCALE = {
    'ACT':              2.4,
    'AIMA':             2.0,
    'AKTO':             1.8,
    'APF':              2.1,
    'Saúde em Português': 1.8,
    'CIG':              2.8,
    'CPR':              2.0,
    'JRS':              1.9,
    'OIM':              3.5,
    'OTSH':             2.2,
}

const orgs = [
    { abbr: 'ACT', href: 'https://portal.act.gov.pt/Pages/Home.aspx', img: '/assets/images/orgs/ACT-1-scaled.png' },
    { abbr: 'AIMA', href: 'https://aima.gov.pt/pt', img: '/assets/images/orgs/AIMA.png' },
    { abbr: 'AKTO', href: 'https://www.akto.org/pt/o-nosso-trabalho/', img: '/assets/images/orgs/AKTO-1-scaled.png' },
    { abbr: 'APF', href: 'https://apf.pt/informacao-tematica/violencia-sexual-e-de-genero/trafico-de-seres-humanos/#contactosdeapoioavitimadetsh', img: '/assets/images/orgs/APF-2-scaled.png' },
    { abbr: 'Saúde em Português', href: 'https://www.saudeportugues.org/portfolio_item/centro-de-acolhimento-e-protecao-para-vitimas-de-trafico-de-seres-humanos-do-sexo-masculino/', img: '/assets/images/orgs/SP-scaled.png' },
    { abbr: 'CIG', href: 'https://www.cig.gov.pt/', img: '/assets/images/orgs/CIG.png' },
    { abbr: 'CPR', href: 'https://cpr.pt/', img: '/assets/images/orgs/CPR.png' },
    { abbr: 'JRS', href: 'https://www.jrsportugal.pt/', img: '/assets/images/orgs/JRS-1-scaled.png' },
    { abbr: 'OIM', href: 'https://portugal.iom.int/pt-pt/oim-em-portugal', img: '/assets/images/orgs/OIM_PT.png' },
    { abbr: 'OTSH', href: 'https://www.otsh.mai.gov.pt/', img: '/assets/images/orgs/OTSH.png' },
]

export default function InformacoesUteis() {
    const { t } = useTranslation()
    const nomes = t('informacoesUteis.orgs', { returnObjects: true })
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            <title>{t('informacoesUteis.metaTitle')}</title>
            <meta name="description" content={t('informacoesUteis.metaDescription')} />
            {/* pageUrl() já inclui a base da língua (/, /en/, /uk/, …) */}
            <link rel="canonical" href={pageUrl('informacoes-uteis')} />
            <meta property="og:url" content={pageUrl('informacoes-uteis')} />
            {/* Hero */}
            <section className="py-16 lg:py-24" style={{ backgroundColor: '#00203F' }}>
                <div className="container-custom">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="font-heading text-4xl lg:text-5xl font-bold text-white leading-tight"
                    >
                        {t('informacoesUteis.h1')}
                    </motion.h1>
                    <div className="mt-6 h-1 w-24 rounded-full" style={{ backgroundColor: '#ADEFD1' }} />
                </div>
            </section>

            {/* Content */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-6xl mx-auto"
                    >
                        <p className="leading-relaxed mb-12 text-base max-w-2xl" style={{ color: '#00203F' }}>{t('informacoesUteis.intro')}</p>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {orgs.map((org, i) => {
                                const scale = LOGO_SCALE[org.abbr] ?? 1
                                return (
                                    <motion.a
                                        key={org.abbr}
                                        href={org.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variants={fadeUp}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: i * 0.05 }}
                                        className="group flex flex-col rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                                        style={{ border: '1px solid rgba(0,32,63,0.12)', boxShadow: '0 2px 8px rgba(0,32,63,0.06)' }}
                                    >
                                        {/* Logo area — fixed height, overflow hidden, scale per logo */}
                                        <div
                                            className="bg-white flex items-center justify-center overflow-hidden"
                                            style={{ height: '180px' }}
                                        >
                                            <img
                                                src={org.img}
                                                alt={org.abbr}
                                                style={{
                                                    maxWidth: '70%',
                                                    maxHeight: '110px',
                                                    objectFit: 'contain',
                                                    transform: `scale(${scale})`,
                                                    transformOrigin: 'center center',
                                                }}
                                            />
                                        </div>

                                        {/* Info area */}
                                        <div
                                            className="flex flex-col flex-1 p-5 gap-3"
                                            style={{ backgroundColor: '#00203F' }}
                                        >
                                            <div>
                                                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#ADEFD1' }}>
                                                    {org.abbr}
                                                </p>
                                                <p className="text-sm leading-snug font-medium text-white/90">
                                                    {nomes[i]}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-auto text-xs font-bold" style={{ color: '#ADEFD1' }}>
                                                <span>{t('informacoesUteis.visitar')}</span>
                                                <ExternalLink size={12} />
                                            </div>
                                        </div>
                                    </motion.a>
                                )
                            })}
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
